import { useCallback } from "react";

import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { z } from "zod";

/**
 * Shared chrome for internal dev-only panels (e.g. billing `DebugOverlay`, chat
 * session link stack). Uses the lattice `debug` tone tokens so every dev panel
 * reads with the same deliberately off-palette "this is a dev tool" treatment as
 * the shared `DebugSurface`.
 */
export const DEBUG_DEV_PANEL_BASE_CLASS =
  "rounded-md border-2 border-debug-border bg-debug-soft shadow-lg";

const STORAGE_KEY = "replo.debug.overlayIsVisible";

export function useDebugOverlayIsVisible(): {
  visible: boolean;
  toggle: () => void;
} {
  const [visible, setVisible] = useLocalStorageState(STORAGE_KEY, false, {
    schema: z.boolean(),
  });

  const toggle = useCallback(() => {
    setVisible((previous) => !previous);
  }, [setVisible]);

  return { visible: visible ?? false, toggle };
}

/**
 * Non-reactive read of the same flag exposed by `useDebugOverlayIsVisible`.
 * Use this only outside React render (callbacks, postMessage handlers, etc.);
 * inside components prefer the hook so they re-render on toggle.
 */
export function shouldShowDebugOverlays(): boolean {
  return (
    typeof window !== "undefined" &&
    localStorage.getItem(STORAGE_KEY) === "true"
  );
}
